import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../context/ThemeContext';
import { api } from '../lib/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark, toggleTheme } = useTheme();

  const loginMutation = useMutation({
    mutationFn: () => api.auth.login(email, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      window.location.href = '/';
    },
  });

  const registerMutation = useMutation({
    mutationFn: () => api.auth.register(email, password, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      window.location.href = '/';
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isRegister) {
      registerMutation.mutate();
    } else {
      loginMutation.mutate();
    }
  };

  const error = loginMutation.error || registerMutation.error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sonarges-gray-50 to-sonarges-green-50 dark:from-sonarges-gray-900 dark:to-sonarges-gray-800 flex items-center justify-center px-4 transition-colors duration-300">
      <button
        onClick={toggleTheme}
        className="absolute top-6 right-6 p-3 rounded-full bg-white dark:bg-sonarges-gray-800 shadow-lg hover:shadow-xl transition-all duration-200 group"
        aria-label="Toggle theme"
      >
        {isDark ? (
          <svg className="w-6 h-6 text-sonarges-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-sonarges-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-sonarges-gray-800 rounded-2xl shadow-2xl p-8 md:p-10 transition-colors duration-300">
          <div className="flex justify-center mb-8">
            <img
              src={isDark ? "/logo-sonarges-white.png" : "/logo-sonarges-color.png"}
              alt="SONARGES Logo"
              className="h-20 w-auto object-contain"
            />
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-center mb-2 text-sonarges-gray-900 dark:text-white">
            Plateforme de Gestion
          </h1>
          <p className="text-center text-sonarges-gray-600 dark:text-sonarges-gray-400 mb-8">
            Système de gestion des réunions et documents
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <label className="block text-sm font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300 mb-2">
                  Nom complet
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white placeholder-sonarges-gray-400 transition-colors duration-200"
                  placeholder="Votre nom"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white placeholder-sonarges-gray-400 transition-colors duration-200"
                placeholder="exemple@sonarges.tn"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300 mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white placeholder-sonarges-gray-400 transition-colors duration-200"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="bg-sonarges-red-50 dark:bg-sonarges-red-900/20 border-l-4 border-sonarges-red-500 p-4 rounded">
                <p className="text-sonarges-red-700 dark:text-sonarges-red-400 text-sm font-medium">
                  {error.message}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loginMutation.isPending || registerMutation.isPending}
              className="w-full bg-gradient-to-r from-sonarges-green-600 to-sonarges-green-500 text-white font-semibold py-3 rounded-lg hover:from-sonarges-green-700 hover:to-sonarges-green-600 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
            >
              {loginMutation.isPending || registerMutation.isPending ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Chargement...
                </span>
              ) : (
                <span>{isRegister ? "S'inscrire" : 'Se connecter'}</span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-sonarges-green-600 dark:text-sonarges-green-400 text-sm font-medium hover:underline transition-colors"
            >
              {isRegister
                ? 'Déjà un compte? Se connecter'
                : "Pas de compte? S'inscrire"}
            </button>
          </div>

          <div className="mt-8 p-5 bg-gradient-to-r from-sonarges-gray-50 to-sonarges-green-50 dark:from-sonarges-gray-700 dark:to-sonarges-gray-600 rounded-xl border border-sonarges-gray-200 dark:border-sonarges-gray-600">
            <p className="font-semibold text-sonarges-gray-800 dark:text-white mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-sonarges-green-600 dark:text-sonarges-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Comptes de démonstration
            </p>
            <div className="space-y-2 text-sm text-sonarges-gray-700 dark:text-sonarges-gray-300">
              <div className="flex items-start">
                <span className="inline-block w-24 font-semibold text-sonarges-red-600 dark:text-sonarges-red-400">Admin:</span>
                <span>admin@example.com / admin123</span>
              </div>
              <div className="flex items-start">
                <span className="inline-block w-24 font-semibold text-sonarges-green-600 dark:text-sonarges-green-400">Organizer:</span>
                <span>organizer@example.com / user123</span>
              </div>
              <div className="flex items-start">
                <span className="inline-block w-24 font-semibold text-sonarges-gray-600 dark:text-sonarges-gray-400">Member:</span>
                <span>member@example.com / user123</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-sonarges-gray-600 dark:text-sonarges-gray-400">
          <p>© 2025 SONARGES - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
}
